import { useState, useEffect, useMemo } from "react";
import DataTable from "../../Common/DataTable/DataTable";
import {
  LienBeneficiary,
  mcbPartnerAccount,
  mcbPartnerWallets,
  mcbGlobalAccount,
  lienToUnlienTransfer,
  mcbPartnerAddBeneficiaryList,
  mcbUserAddBeneficiary,
  commisionStatus,
  changeCommissionStatus,
} from "../../api";
import CommissionConfigModal from "./CommissionConfigModal";
import DataLoader from "../../Common/DataLoader/DataLoader";
import Button from "../../Common/Button";
import { Offcanvas, Accordion } from "react-bootstrap";
import LienAddBeneficiary from "./LienAddBeneficiary";
import LienTransferFlow from "./LienTransferFlow";
import styles from "./beneficiary.module.css";
import withFeatureActions from "../../withFeatureActions";
import {
  lienBeneficiaryColumns,
  MCBPartnerAddBeneficiaryColumns,
} from "../../utils/tableHeader";
import TransferStep1 from "./TransferStep1";
import TransferStep2 from "./TransferStep2";
import TransferStep3 from "./TransferStep3";
import {formatTransferDate, formatAmountWithDecimal} from "../../../src/Constants/helper"

// const formatTransferDate = () => {
//   const now = new Date();
//   return now.toLocaleString("en-GB", {
//     day: "2-digit",
//     month: "2-digit",
//     year: "numeric",
//     hour: "2-digit",
//     minute: "2-digit",
//     hour12: true,
//   });
// };

const Transfers = ({ userId, userStatus, featureAccess, detail }) => {
  console.log("Transfers component rendered with userId:", userStatus);
  const { writeAccess, modifyAccess } = featureAccess;

  const [dataLoading, setDataLoading] = useState(false);
  const [beneficiaryData, setBeneficiaryData] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateBeneficiary, setShowCreateBeneficiary] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showLienUnlienTransfer, setShowLienUnlienTransfer] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [canAddBeneficiary, setCanAddBeneficiary] = useState(false);
  const [usdLienBalance, setUsdLienBalance] = useState(0);
  const [lienAccountNo, setLienAccountNo] = useState("");
  const [unlienAccountNo, setUnlienAccountNo] = useState("");
  const [lienUnlienStep, setLienUnlienStep] = useState(1);
  const [transferFormData, setTransferFormData] = useState({
    amount: "",
    remarks: "",
  });
  const [transferOn, setTransferOn] = useState("");
  const [transferId, setTransferId] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [canTransferToUnlien, setCanTransferToUnlien] = useState(false);
  const [partnerAddBeneficiaryData, setPartnerAddBeneficiaryData] = useState([],);
  const [selectedPartnerBeneficiaries, setSelectedPartnerBeneficiaries] = useState([]);
  const [isPartnerSubmitting, setIsPartnerSubmitting] = useState(false);
  const [partnerSuccessMessage, setPartnerSuccessMessage] = useState("");
  const [partnerErrorMessage, setPartnerErrorMessage] = useState(null);
  const [partnerSuccessTimeout, setPartnerSuccessTimeout] = useState(null);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [commissionBeneficiary, setCommissionBeneficiary] = useState(null);
  const [commissionStatusLoading, setCommissionStatusLoading] = useState(false);
  const [commissionSubmitLoading, setCommissionSubmitLoading] = useState(false);
  const [commissionIsActive, setCommissionIsActive] = useState(false);
  const [commissionError, setCommissionError] = useState("");
  const [commissionSuccess, setCommissionSuccess] = useState(false);

  const fetchBeneficiaryDetails = async (page = 1) => {
    try {
      setDataLoading(true);

      const response = await LienBeneficiary(userId, page, pageSize, "");

      if (response?.status === 200) {
        const beneficiaryList = response?.data?.data?.beneficiaries || [];
        const count = response?.data?.data?.count || 0;

        setBeneficiaryData(beneficiaryList);
        setTotalPages(Math.ceil(count / pageSize));
        setErrorMessage(null);
      } else {
        setErrorMessage(
          response?.data?.message || "Failed to fetch beneficiary details",
        );
      }
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Something went wrong");
    } finally {
      setDataLoading(false);
    }
  };

  const fetchPartnerAddBeneficiaryDetails = async () => {
    try {
      setDataLoading(true);
      const response = await mcbPartnerAddBeneficiaryList(userId);
      if (response?.status === 200) {
        const partnerAddBeneficiaryList = response?.data?.data || [];
        setPartnerAddBeneficiaryData(partnerAddBeneficiaryList);
        setPartnerErrorMessage(null);
      } else {
        setPartnerErrorMessage(
          response?.data?.message ||
            "Failed to fetch partner beneficiary details",
        );
      }
    } catch (error) {
      setPartnerErrorMessage(
        error?.response?.data?.message || "Something went wrong",
      );
    } finally {
      setDataLoading(false);
    }
  };

  const checkPartnerEligibility = async () => {
    try {
      const [accountRes, walletRes, globalRes] = await Promise.all([
        mcbPartnerAccount(userId),
        mcbPartnerWallets(userId),
        mcbGlobalAccount(userId),
      ]);
      if (accountRes?.status === 200) {
        const partnerData = accountRes?.data?.data;
        const accountStatus = partnerData?.status;
        const globalStatus = globalRes?.data?.data?.status;

        if (partnerData?.vendorAccountNo) {
          setLienAccountNo(partnerData.vendorAccountNo);
        }
        if (globalRes?.status === 200) {
          const globalData = globalRes?.data?.data;
          if (globalData?.vendorAccountNo) {
            setUnlienAccountNo(globalData.vendorAccountNo);
          }
        }
        let hasWalletBalance = false;
        if (walletRes?.status === 200) {
          const wallets = walletRes?.data?.data?.accounts || [];
          const usdWallet = wallets.find((w) => w.vendorCurrency === "USD");
          if (usdWallet) {
            const balance = parseFloat(usdWallet.balance) || 0;
            const holdBalance = parseFloat(usdWallet.holdBalance) || 0;
            setUsdLienBalance(balance - holdBalance);
          }

          hasWalletBalance = wallets.some((w) => parseFloat(w.balance) > 0);
        }
        setCanAddBeneficiary(accountStatus === "APPROVED" && hasWalletBalance);
        
        setCanTransferToUnlien(
          accountStatus === "APPROVED" && globalStatus === "APPROVED"
        );
      } else {
        setCanAddBeneficiary(false);
      }
    } catch (error) {
      setCanAddBeneficiary(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchBeneficiaryDetails(currentPage);
      checkPartnerEligibility();
    }
  }, [userId, currentPage]);

  useEffect(() => {
    if (userId) {
      fetchPartnerAddBeneficiaryDetails();
    }
  }, [userId]);

  const handleCreateBeneficiary = (newBeneficiary) => {
    setBeneficiaryData((prev) => [newBeneficiary, ...prev]);
    setShowCreateBeneficiary(false);
    fetchBeneficiaryDetails(currentPage);
  };

  const handleTransferNow = (item) => {
    setSelectedBeneficiary(item);
    setShowTransfer(true);
  };

  const handleTransferClose = () => {
    setShowTransfer(false);
    setSelectedBeneficiary(null);
    fetchBeneficiaryDetails(currentPage);
  };
  const handleCommissionConfig = async (item) => {
    setCommissionBeneficiary(item);
    setShowCommissionModal(true);
    setCommissionError("");
    setCommissionSuccess(false);
    setCommissionStatusLoading(true);

    try {
      const response = await commisionStatus(
        item.userId,
        item.country,
        item.method,
      );

      if (response?.status === 200) {
        setCommissionIsActive(response?.data?.data?.status === "ACTIVE");
      } else {
        setCommissionError(
          response?.data?.message || "Failed to fetch commission status",
        );
      }
    } catch (error) {
      setCommissionError("Something went wrong");
    } finally {
      setCommissionStatusLoading(false);
    }
  };

  const handleCommissionModalClose = () => {
    setShowCommissionModal(false);
    setCommissionBeneficiary(null);
    setCommissionError("");
    setCommissionSuccess(false);
  };

  const handleCommissionToggle = (checked) => {
    setCommissionIsActive(checked);
  };

  const handleCommissionSubmit = async () => {
    if (!commissionBeneficiary) return;

    setCommissionSubmitLoading(true);
    setCommissionError("");

    try {
      const payload = {
        userId: commissionBeneficiary.userId,
        country: commissionBeneficiary.country,
        transactionType: "TRANSFER",
        method: commissionBeneficiary.method,
        status: commissionIsActive ? "ACTIVE" : "INACTIVE",
      };

      const response = await changeCommissionStatus(payload);

      if (response?.status === 200) {
        setCommissionSuccess(true);
        setTimeout(() => {
          handleCommissionModalClose();
        }, 2000);
      } else {
        setCommissionError(
          response?.data?.message || "Failed to update commission status",
        );
      }
    } catch (error) {
      setCommissionError("Something went wrong");
    } finally {
      setCommissionSubmitLoading(false);
    }
  };

  const openLienUnlienOffcanvas = () => {
    setLienUnlienStep(1);
    setTransferFormData({ amount: "", remarks: "" });
    setTransferOn("");
    setTransferId("");
    setShowLienUnlienTransfer(true);
  };

  const closeLienUnlienOffcanvas = () => {
    setShowLienUnlienTransfer(false);
    setTimeout(() => setLienUnlienStep(1), 300);
  };

  const handleStep1Next = ({ amount, remarks }) => {
    setTransferFormData({ amount, remarks });
    setTransferOn(formatTransferDate());
    setLienUnlienStep(2);
  };

  const handleStep2Confirm = async () => {
    try {
      setConfirmLoading(true);

      const payload = {
        userId,
        remarks: transferFormData.remarks,
        currency: "USD",
        amount: parseFloat(transferFormData.amount),
        purpose: "Lien Release",
      };

      const res = await lienToUnlienTransfer(payload);

      if (res?.status === 200) {
        const resData = res?.data?.data;
        setTransferId(resData?.transactionNo || "");
        setLienAccountNo(resData?.fromAccount || lienAccountNo);
        setUnlienAccountNo(resData?.toAccount || unlienAccountNo);
        setTransferOn(formatTransferDate(new Date(resData?.createdAt)));
        setLienUnlienStep(3);

        checkPartnerEligibility();
      } else {
        setErrorMessage(
          res?.data?.message || "Transfer failed. Please try again.",
        );
      }
    } catch {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setConfirmLoading(false);
    }
  };

  const handlePartnerSelectionChange = (beneficiary, isChecked) => {
    if (isChecked) {
      setSelectedPartnerBeneficiaries([beneficiary]);
    } else {
      setSelectedPartnerBeneficiaries([]);
    }
  };

  const handleSubmitSelectedPartner = async () => {
    if (selectedPartnerBeneficiaries.length === 0) {
      setPartnerErrorMessage("Please select beneficiary account");
      setTimeout(() => {
        setPartnerErrorMessage(null);
      }, 5000);
      return;
    }

    setIsPartnerSubmitting(true);

    try {
      const beneficiary = selectedPartnerBeneficiaries[0];

      const payload = {
        userId: userId,
        partnerBeneficiaryId: beneficiary.beneficiaryId,
      };

      const response = await mcbUserAddBeneficiary(payload);

      if (response.status === 200) {
        if (partnerSuccessTimeout) {
          clearTimeout(partnerSuccessTimeout);
        }
        setPartnerSuccessMessage("Beneficiary added successfully!");
        setSelectedPartnerBeneficiaries([]);
        const timeout = setTimeout(() => {
          setPartnerSuccessMessage("");
          fetchBeneficiaryDetails(currentPage);
          fetchPartnerAddBeneficiaryDetails();
          setPartnerSuccessTimeout(null);
        }, 3000);
        setPartnerSuccessTimeout(timeout);
      } else {
        setPartnerErrorMessage(
          response?.data?.message || "Failed to add beneficiary.",
        );
      }

      setTimeout(() => {
        setPartnerErrorMessage(null);
      }, 5000);
    } catch (error) {
      setPartnerErrorMessage(
        error?.response?.data?.message || "Error adding beneficiary.",
      );
      setTimeout(() => {
        setPartnerErrorMessage(null);
      }, 5000);
    } finally {
      setIsPartnerSubmitting(false);
    }
  };

  return (
    <DataLoader isLoading={dataLoading}>
      <Accordion
        defaultActiveKey={["0"]}
        alwaysOpen
        className="mb-5 accordionContainer"
      >
        <Accordion.Item eventKey="0" className="accordionItem">
          <Accordion.Header className="accordionHeader">
            Lien to Unlien Transfer
          </Accordion.Header>
          <Accordion.Body className="accordionBody">
            <div className="d-flex justify-content-between align-items-center">
              <div className="gap-2 d-flex flex-column">
                <div className={styles.lienText}>Lien Balance</div>
                <div className={styles.lienAmount}>{formatAmountWithDecimal(usdLienBalance)}</div>
              </div>
              {modifyAccess && canTransferToUnlien && (
              <Button
                onClick={openLienUnlienOffcanvas}
                title="Transfer to Unlien"
                classes={styles.transferButton}
              />
              )}
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      <Accordion
        defaultActiveKey={["0"]}
        alwaysOpen
        className="accordionContainer"
      >
        <Accordion.Item eventKey="0" className="accordionItem">
          <Accordion.Header className="accordionHeader">
            Beneficiary Management
          </Accordion.Header>
          <Accordion.Body className="accordionBody">
            <DataTable
              columns={lienBeneficiaryColumns(featureAccess)}
              list={beneficiaryData}
              rowClick={false}
              totalPages={totalPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pagination={true}
              title="Beneficiary List"
              titleButton={
                writeAccess && canAddBeneficiary ? (
                  <Button
                    onClick={() => setShowCreateBeneficiary(true)}
                    title="Add Beneficiary"
                  />
                ) : null
              }
              handleTransferNow={handleTransferNow}
              handleCommissionConfig={handleCommissionConfig}
            />
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      {writeAccess && userStatus === "APPROVED" && (
        <div style={{ marginTop: "30px" }}>
          <DataTable
            columns={MCBPartnerAddBeneficiaryColumns}
            list={partnerAddBeneficiaryData}
            rowClick={false}
            title="Partner Available Beneficiary Account"
            enableCheckbox={true}
            selectedItems={selectedPartnerBeneficiaries}
            onSelectionChange={handlePartnerSelectionChange}
            submitButton={
              <Button
                onClick={handleSubmitSelectedPartner}
                title={"Add Selected Beneficiary"}
                loading={isPartnerSubmitting}
                disabled={isPartnerSubmitting}
              />
            }
            successMessage={partnerSuccessMessage}
            errorMessage={partnerErrorMessage}
          />
        </div>
      )}

      <Offcanvas
        show={showCreateBeneficiary}
        onHide={() => setShowCreateBeneficiary(false)}
        placement="end"
        className={styles.offcanvas}
        style={{
          color: "white",
          backgroundColor: "#28282B",
          width: "650px",
        }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ color: "white" }}>
            Add Beneficiary
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          <LienAddBeneficiary
            userId={userId}
            onSuccess={handleCreateBeneficiary}
            onCancel={() => setShowCreateBeneficiary(false)}
            existingData={selectedBeneficiary}
          />
        </Offcanvas.Body>
      </Offcanvas>
      <Offcanvas
        show={showTransfer}
        onHide={handleTransferClose}
        placement="end"
        className={styles.offcanvas}
        style={{
          color: "white",
          backgroundColor: "#28282B",
          width: "650px",
        }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ color: "white" }}>
            Send Money To
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {selectedBeneficiary && (
            <LienTransferFlow
              beneficiary={selectedBeneficiary}
              userId={userId}
              onClose={handleTransferClose}
            />
          )}
        </Offcanvas.Body>
      </Offcanvas>

      <Offcanvas
        show={showLienUnlienTransfer}
        onHide={closeLienUnlienOffcanvas}
        placement="end"
        className={styles.offcanvas}
        style={{
          color: "white",
          backgroundColor: "#28282B",
          width: "650px",
        }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ color: "white" }}>
            Lien to Unlien Transfer
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body style={{ padding: 0 }}>
          {lienUnlienStep === 1 && (
            <TransferStep1
              lienAccountNo={lienAccountNo}
              unlienAccountNo={unlienAccountNo}
              lienBalance={usdLienBalance}
              userName={detail?.name || ""}
              onNext={handleStep1Next}
              onCancel={closeLienUnlienOffcanvas}
              formData={transferFormData}
              setFormData={setTransferFormData}
            />
          )}

          {lienUnlienStep === 2 && (
            <TransferStep2
              amount={transferFormData.amount}
              remarks={transferFormData.remarks}
              lienAccountNo={lienAccountNo}
              unlienAccountNo={unlienAccountNo}
              name={detail?.name || ""}
              transferOn={transferOn}
              onBack={() => setLienUnlienStep(1)}
              onConfirm={handleStep2Confirm}
              loading={confirmLoading}
            />
          )}

          {lienUnlienStep === 3 && (
            <TransferStep3
              transferId={transferId}
              amount={transferFormData.amount}
              remarks={transferFormData.remarks}
              lienAccountNo={lienAccountNo}
              unlienAccountNo={unlienAccountNo}
              name={detail?.name || ""}
              transferOn={transferOn}
            />
          )}
        </Offcanvas.Body>
      </Offcanvas>
      <CommissionConfigModal
        isOpen={showCommissionModal}
        onRequestClose={handleCommissionModalClose}
        onSubmit={handleCommissionSubmit}
        statusLoading={commissionStatusLoading}
        submitLoading={commissionSubmitLoading}
        isActive={commissionIsActive}
        onToggle={handleCommissionToggle}
        beneficiaryName={
          commissionBeneficiary?.nameOfBeni || commissionBeneficiary?.name || ""
        }
        showSuccess={commissionSuccess}
        errorMessage={commissionError}
      />
    </DataLoader>
  );
};

export default withFeatureActions(Transfers, "MCB", "Transfers");
